// ═══════════════════════════════════════════════════════════════════════════
// AUREUS — « EST-CE QUE JE PAIE ASSEZ ? » (logique PURE, testée)
// ───────────────────────────────────────────────────────────────────────────
// POURQUOI CE MODULE EXISTE. Sur les requêtes « barème CP … », les cinq
// premiers résultats de Google sont des PDF (FGTB, CSC, CGSLB, fiche Limosa du
// SPF). Un PDF donne un tableau ; il ne répond pas à la question que l'employeur
// se pose réellement, qui est « et MOI, est-ce que je suis en règle ? ».
// C'est la seule chose qu'une page vivante peut faire et qu'un PDF ne fera
// jamais — et le calcul existait déjà côté app, il ne sortait simplement pas.
//
// 🛡️ TROIS HONNÊTETÉS, NON NÉGOCIABLES (R2 bis : on n'invente pas un mécanisme).
//
//  1. Le minimum publié est un minimum d'ENTRÉE : 0 an d'ancienneté, catégorie
//     la plus basse de la grille. Un travailleur avec de l'ancienneté ou une
//     fonction plus élevée a droit à PLUS. Le verdict ne dit donc JAMAIS « vous
//     êtes en règle » — il dit « au-dessus du minimum d'entrée », ce qui est la
//     seule chose que la donnée permet d'affirmer.
//  2. Le RMMMG (minimum national) reste un plancher absolu par-dessus le
//     sectoriel. On le compare aussi quand il est fourni, et on retient le PLUS
//     ÉLEVÉ des deux — c'est celui qui s'impose.
//  3. Sans minimum publié pour cette CP, on ne devine pas : on rend
//     `statut: 'sans-bareme'`. Une fiche honnêtement vide vaut mieux qu'un
//     verdict plausible (R12).
//
// Le prorata temps partiel se fait sur les heures RÉELLES / heures de
// RÉFÉRENCE du secteur, toutes deux fournies par l'appelant — jamais supposées
// ici. Verrou : __tests__/verif-salaire-minimum.test.js
// ═══════════════════════════════════════════════════════════════════════════
import { round2 } from '@/app/lib/money';

/**
 * @param {object} p
 * @param {number} p.brutMensuel        brut mensuel déclaré par l'employeur
 * @param {number|null} p.minimumSecteur minimum d'entrée temps plein de la CP
 * @param {number|null} [p.rmmmg]        minimum national temps plein (plancher absolu)
 * @param {number|null} [p.heuresTravaillees] heures/semaine du travailleur
 * @param {number|null} [p.heuresReference]   heures/semaine du temps plein sectoriel
 * @returns {{statut:string, minimumApplicable:number|null, ecart:number|null,
 *            prorata:number|null, plancherRetenu:string|null}}
 */
export function verifierSalaire({
  brutMensuel,
  minimumSecteur,
  rmmmg = null,
  heuresTravaillees = null,
  heuresReference = null,
}) {
  const vide = { statut: 'sans-bareme', minimumApplicable: null, ecart: null, prorata: null, plancherRetenu: null };

  const secteur = Number(minimumSecteur);
  const national = Number(rmmmg);
  const aSecteur = Number.isFinite(secteur) && secteur > 0;
  const aNational = Number.isFinite(national) && national > 0;
  if (!aSecteur && !aNational) return vide;

  // Le plancher qui s'impose est le PLUS ÉLEVÉ des deux : le RMMMG ne
  // « remplace » pas un barème sectoriel plus généreux, et un barème
  // sectoriel ne permet pas de descendre sous le minimum national.
  let base, plancherRetenu;
  if (aSecteur && aNational) {
    base = Math.max(secteur, national);
    plancherRetenu = secteur >= national ? 'secteur' : 'national';
  } else if (aSecteur) { base = secteur; plancherRetenu = 'secteur'; }
  else { base = national; plancherRetenu = 'national'; }

  // Prorata : uniquement si les DEUX durées sont fournies et cohérentes.
  // Sans elles, on reste en temps plein — on ne suppose pas 38 h, parce que
  // le temps plein sectoriel vaut 37,5 h ou 39 h dans plusieurs commissions.
  let prorata = null;
  const ht = Number(heuresTravaillees);
  const hr = Number(heuresReference);
  if (Number.isFinite(ht) && Number.isFinite(hr) && ht > 0 && hr > 0) {
    prorata = ht / hr;
    base = base * prorata;
  }
  const minimumApplicable = round2(base);

  const brut = Number(brutMensuel);
  if (!Number.isFinite(brut) || brut <= 0) {
    return { statut: 'incomplet', minimumApplicable, ecart: null, prorata, plancherRetenu };
  }

  const ecart = round2(brut - minimumApplicable);
  // Un centime d'écart est du bruit d'arrondi, pas une infraction : on ne
  // crie « en dessous » qu'au-delà. Dans l'autre sens, « au niveau exact du
  // minimum » se dit, plutôt que de se faire passer pour un confort.
  let statut;
  if (ecart < -0.01) statut = 'sous-le-minimum';
  else if (ecart <= 0.01) statut = 'au-minimum';
  else statut = 'au-dessus';

  return { statut, minimumApplicable, ecart, prorata, plancherRetenu };
}
